import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Alert, CircularProgress, Box, Typography,
    ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import { financialAccountService } from "../../services/financialAccountService";
import { FinancialAccountWithBalance, AccountAdjustmentCreate } from "../../types/accounts";
import { formatCurrencyBDT } from "../../utils/currency";

interface AdjustmentDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** The account this adjustment applies to — fixed, no picker. Keeping
     * "which account" out of this form entirely removes the one mistake a
     * dropdown could introduce for an action that has no second account to
     * sanity-check against (unlike a transfer). */
    account: FinancialAccountWithBalance | null;
}

type Direction = "increase" | "decrease";

const todayDateInput = () => new Date().toISOString().split("T")[0];

const defaultForm = () => ({
    direction: "increase" as Direction,
    magnitude: "",
    adjustment_date: todayDateInput(),
    reason: "",
});

export const AdjustmentDialog: React.FC<AdjustmentDialogProps> = ({
    open, onClose, onSuccess, account,
}) => {
    const [form, setForm] = useState(defaultForm());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm(defaultForm());
            setError("");
        }
    }, [open, account?.account_id]);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const magnitudeNum = parseFloat(form.magnitude);
    const signedAmount =
        !isNaN(magnitudeNum) && magnitudeNum > 0
            ? (form.direction === "increase" ? magnitudeNum : -magnitudeNum)
            : null;
    const previewBalance =
        account && signedAmount !== null ? account.balance + signedAmount : null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!account) return;

        if (!magnitudeNum || magnitudeNum <= 0) {
            setError("Enter an amount greater than 0");
            return;
        }
        if (!form.reason.trim()) {
            setError("A reason is required for every balance adjustment");
            return;
        }

        try {
            setSaving(true);
            const data: AccountAdjustmentCreate = {
                amount: form.direction === "increase" ? magnitudeNum : -magnitudeNum,
                reason: form.reason.trim(),
                adjustment_date: `${form.adjustment_date}T00:00:00`,
            };
            await financialAccountService.createAdjustment(account.account_id, data);
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to record adjustment");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Adjust Balance{account ? ` — ${account.name}` : ""}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <Alert severity="warning">
                            Manual adjustments should be rare — prefer recording the real transaction
                            (a payment, expense, or transfer) whenever possible. This directly overrides
                            the computed balance and is permanent; a mistake is corrected with a new,
                            opposite adjustment, not by editing this one.
                        </Alert>

                        {account?.type === "LOAN" && (
                            <Alert severity="info">
                                This is a loan account: "Increase" raises the amount owed,
                                "Decrease" reduces the amount owed.
                            </Alert>
                        )}

                        <ToggleButtonGroup
                            value={form.direction}
                            exclusive
                            fullWidth
                            onChange={(_, val) => val && setForm((f) => ({ ...f, direction: val }))}
                        >
                            <ToggleButton value="increase" color="success">
                                <TrendingUp sx={{ mr: 1 }} fontSize="small" />
                                Increase balance
                            </ToggleButton>
                            <ToggleButton value="decrease" color="error">
                                <TrendingDown sx={{ mr: 1 }} fontSize="small" />
                                Decrease balance
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            type="number" label="Amount (৳)" value={form.magnitude}
                            onChange={(e) => setForm((f) => ({ ...f, magnitude: e.target.value }))}
                            fullWidth required inputProps={{ min: 0, step: 0.01 }}
                        />

                        {previewBalance !== null && account && (
                            <Typography variant="body2" color="text.secondary">
                                New balance will be <strong>{formatCurrencyBDT(previewBalance)}</strong>
                                {" "}(currently {formatCurrencyBDT(account.balance)})
                            </Typography>
                        )}

                        <TextField
                            type="date" label="Adjustment Date" value={form.adjustment_date}
                            onChange={(e) => setForm((f) => ({ ...f, adjustment_date: e.target.value }))}
                            fullWidth required InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label="Reason" value={form.reason}
                            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                            fullWidth required multiline rows={3}
                            placeholder="e.g. Cash count on 2026-09-28 came up ৳250 short — reconciling."
                            helperText="Required — shown in this account's transaction history."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" color="warning" disabled={saving}>
                        {saving ? <CircularProgress size={22} /> : "Apply Adjustment"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
